/**
 * Amplify Hosted-UI OAuth configuration — Google sign-in door only.
 *
 * Implements google-sign-in/US-001 (T1, AC-001.2). This module is the FIRST real
 * Amplify.configure() call in the codebase (confirmed zero prior non-test usage,
 * search_evidence in US-001-technical-implementation-plan.json T1). It configures
 * AWS Amplify v6 to drive the Cognito Hosted-UI authorization-code flow for the
 * "Continue with Google" button ONLY — the existing hand-rolled Zustand/axios auth
 * path (useAuth.ts, authApi.ts) is untouched and remains the auth model for
 * email/password login.
 *
 * BR-001.1: no client secret is ever read or referenced here — only the public
 * app client id + Hosted-UI domain. Cognito performs the code-for-token exchange.
 *
 * Enforces TO-LL-042 (google-sign-in/US-001 kb_lessons_applied): never bake
 * env-specific URLs into the build. redirectSignIn/redirectSignOut are derived
 * from window.location.origin (never a hardcoded per-env string), so ONE build
 * artifact works unmodified on localhost, dev CloudFront, and the prod custom
 * domain. The VITE_COGNITO_* values themselves are injected at build time by
 * scripts/deploy-dev-frontend.sh / .github/workflows/deploy-frontend.yml from
 * CloudFormation outputs (google-sign-in/TS-001), never hardcoded here.
 */

import { Amplify } from 'aws-amplify';
import { ROUTES } from '@/constants/routes';

// google-sign-in/US-001 (T1): read the Hosted-UI OAuth env vars using the exact
// import.meta.env.VITE_* access pattern already established in lib/api.ts:14
// (reused per T1 reuse_details.methods_inherited).
const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID as string | undefined;
const userPoolClientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string | undefined;
// google-sign-in/TS-001 already staged the CI/CD injection under the name
// VITE_COGNITO_HOSTED_UI_DOMAIN (see scripts/deploy-dev-frontend.sh:61,88 and
// .github/workflows/deploy-frontend.yml:122,150) — read that exact variable name
// rather than inventing a new one, per T2 step 1 ("confirm the exact key, do not guess").
const rawHostedUiDomain = import.meta.env.VITE_COGNITO_HOSTED_UI_DOMAIN as string | undefined;

/**
 * True only when every required VITE_COGNITO_* variable is present.
 * Consumed by ContinueWithGoogleButton (T5) to hide the button entirely rather
 * than starting a broken redirect when the environment is mis-provisioned.
 */
export const isOAuthConfigured = Boolean(userPoolId && userPoolClientId && rawHostedUiDomain);

/**
 * The CognitoHostedUiDomainV3 CloudFormation output value is a full URL
 * (`https://<prefix>.auth.<region>.amazoncognito.com`, see backend/template.yaml
 * Outputs.CognitoHostedUiDomainV3), but Amplify's OAuthConfig.domain field expects
 * a bare hostname (no protocol). Strip it defensively here so a future change to
 * either side does not silently break the redirect.
 */
function stripProtocol(domain: string): string {
  return domain.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

/**
 * Derive the per-env redirect URLs from window.location.origin (TO-LL-042) so the
 * SAME built bundle works on localhost:5173, the dev CloudFront domain, and the
 * prod custom domain without any per-env hardcoded string.
 */
export function buildRedirectUrls(): { signIn: string[]; signOut: string[] } {
  const origin = window.location.origin;
  return {
    signIn: [`${origin}${ROUTES.OAUTH_CALLBACK}`],
    signOut: [`${origin}${ROUTES.LOGIN}`],
  };
}

let configured = false;

/**
 * Configure Amplify for the Cognito Hosted-UI Google OAuth flow. Called exactly
 * once from main.tsx before any component renders. Never throws at module load
 * (a mis-provisioned env only disables the Google button — password/email login
 * must keep working, per T1 error_handling).
 */
export function configureAmplifyOAuth(): void {
  if (configured) return; // idempotency guard (T1 error_scenarios_to_handle)

  if (!isOAuthConfigured) {
    const missing = [
      !userPoolId && 'VITE_COGNITO_USER_POOL_ID',
      !userPoolClientId && 'VITE_COGNITO_CLIENT_ID',
      !rawHostedUiDomain && 'VITE_COGNITO_HOSTED_UI_DOMAIN',
    ].filter(Boolean);
    console.error(
      `[amplifyConfig] Google sign-in is disabled — missing env var(s): ${missing.join(', ')}`
    );
    return;
  }

  const { signIn, signOut } = buildRedirectUrls();

  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: userPoolId as string,
        userPoolClientId: userPoolClientId as string,
        loginWith: {
          oauth: {
            domain: stripProtocol(rawHostedUiDomain as string),
            // AC-001.2: exactly these three scopes so the email claim is returned.
            scopes: ['openid', 'email', 'profile'],
            redirectSignIn: signIn,
            redirectSignOut: signOut,
            // AC-001.2 / security_considerations: authorization-code flow ONLY.
            // Never 'token' (implicit flow) — that would expose tokens in the URL
            // fragment (BR-001.1).
            responseType: 'code',
          },
        },
      },
    },
  });

  configured = true;
}
